import React, { useState, useEffect } from 'react';
import { Edit2, Key, ShieldAlert, ShieldCheck } from 'lucide-react';
import { getAllUsers, resetPassword, toggleUserStatus } from '../services/userService';

const UserMgtList = ({ onEdit }) => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pwdModal, setPwdModal] = useState({ open: false, userId: null, newPassword: '' });

    const loadUsers = async () => {
        setLoading(true);
        try {
            const data = await getAllUsers();
            
            // Ensure data is an array before setting state
            if (Array.isArray(data)) {
                setUsers(data);
            } else {
                console.error("Data received is not an array:", data);
                setUsers([]); 
            }
        } catch (err) {
            console.error("Failed to fetch users:", err);
            setUsers([]); // Fallback to empty array on error
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadUsers(); }, []);

    const handlePasswordReset = async () => {
        if (!pwdModal.newPassword) return alert("Enter a new password");
        try {
            await resetPassword(pwdModal.userId, pwdModal.newPassword);
            alert("Password updated successfully!");
            setPwdModal({ open: false, userId: null, newPassword: '' });
        } catch (err) { alert("Error resetting password"); }
    };

    return (
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
            <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 text-gray-600 font-bold uppercase text-xs border-b">
                    <tr>
                        <th className="px-6 py-4">User Details</th>
                        <th className="px-6 py-4">Role</th>
                        <th className="px-6 py-4">Sub-County</th>
                        <th className="px-6 py-4">Status</th>
                        <th className="px-6 py-4 text-center">Actions</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {/* Use a guard to ensure users is an array before mapping */}
                    {Array.isArray(users) && users.map(u => (
                        <tr key={u.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                                <div className="font-bold">{u.username}</div>
                                <div className="text-xs text-gray-500">{u.email}</div>
                            </td>
                            <td className="px-6 py-4 text-xs font-semibold">{u.role}</td>
                            <td className="px-6 py-4">{u.sub_county_name || 'All'}</td>
                            <td className="px-6 py-4">
                                <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${u.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                    {u.status}
                                </span>
                            </td>
                            <td className="px-6 py-4 flex justify-center space-x-2">
                                <button onClick={() => onEdit(u)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"><Edit2 size={16}/></button>
                                <button onClick={() => setPwdModal({ open: true, userId: u.id, newPassword: '' })} className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg"><Key size={16}/></button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Password Change Modal */}
            {pwdModal.open && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
                        <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                            <Key className="mr-2 text-orange-500" /> Force Password Reset
                        </h3>
                        <p className="text-sm text-gray-500 mb-4">Set a new temporary password for this user account.</p>
                        <input 
                            type="password" 
                            placeholder="New Password" 
                            className="w-full border rounded-xl p-3 mb-4 outline-none focus:ring-2 focus:ring-orange-500"
                            value={pwdModal.newPassword}
                            onChange={e => setPwdModal({...pwdModal, newPassword: e.target.value})}
                        />
                        <div className="flex space-x-3">
                            <button onClick={handlePasswordReset} className="flex-1 bg-orange-600 text-white py-3 rounded-xl font-bold">Update Password</button>
                            <button onClick={() => setPwdModal({ open: false, userId: null, newPassword: '' })} className="px-6 py-3 border rounded-xl font-bold text-gray-500">Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserMgtList;